import { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '#/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '#/lib/breakpoints';

import type {
  CategoryNavigationItem,
  CategoryNavigationProps,
  DesktopTabId,
} from './types';
import { useScrollPinning } from './useScrollPinning';

function getDesktopTabForActiveHref(
  activeHref: string | undefined,
  sectors: CategoryNavigationItem[],
): DesktopTabId {
  if (activeHref === undefined) {
    return 'productTypes';
  }

  return sectors.some((sector) => sector.href === activeHref)
    ? 'sectors'
    : 'productTypes';
}

function getResponsiveMode(mode: NonNullable<CategoryNavigationProps['mode']>) {
  return {
    showMobile: mode === 'responsive' || mode === 'mobile',
    showDesktop: mode === 'responsive' || mode === 'desktop',
    forceDesktopLayout: mode === 'desktop',
  };
}

interface UseCategoryNavigationShellOptions {
  activeHref?: string;
  sectors: CategoryNavigationItem[];
  mode: NonNullable<CategoryNavigationProps['mode']>;
  pinned?: boolean;
  onMenuClick?: () => void;
}

/**
 * Consolidates the state machine for the shared category-navigation shell.
 *
 * `CategoryNavigation` renders multiple presentations from one abstraction:
 *
 * - a sticky mobile header with a drawer-style browse experience
 * - a sticky desktop shell with a condensed pinned state and overlay menu
 * - a responsive mode that swaps interaction models at the desktop breakpoint
 *
 * The component itself is easier to read if it can treat those behaviors as a
 * single "shell" contract rather than coordinating several independent hooks
 * and refs inline. This hook provides that contract by combining:
 *
 * - responsive mode resolution (`showMobile`, `showDesktop`,
 *   `forceDesktopLayout`)
 * - viewport-aware interaction enablement so responsive mode behaves like the
 *   correct platform without duplicating breakpoint logic in the component
 * - sticky pinning for both mobile and desktop, including the desktop release
 *   band and transition lock that protect against layout-shift churn at the pin
 *   threshold
 * - desktop overlay state and active-tab state, seeded from the current route
 *   and kept in sync as the active href changes
 *
 * Reviewers can treat the return value as the public behavior surface for the
 * category-navigation shell. The component then focuses on markup and styling,
 * while this hook owns the branching and lifecycle mechanics.
 */
export function useCategoryNavigationShell({
  activeHref,
  sectors,
  mode,
  pinned,
  onMenuClick,
}: UseCategoryNavigationShellOptions) {
  const { showMobile, showDesktop, forceDesktopLayout } =
    getResponsiveMode(mode);
  const isDesktopViewport = useMediaQuery(
    DESKTOP_BREAKPOINT_QUERY,
    mode === 'responsive',
  );
  const mobilePinSentinelRef = useRef<HTMLDivElement | null>(null);
  const desktopPinSentinelRef = useRef<HTMLDivElement | null>(null);
  const desktopPinReleaseOffsetRef = useRef(0);
  const desktopPinTransitionLockRef = useRef(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [activeDesktopTab, setActiveDesktopTab] = useState<DesktopTabId>(() =>
    getDesktopTabForActiveHref(activeHref, sectors),
  );

  useEffect(() => {
    setActiveDesktopTab(getDesktopTabForActiveHref(activeHref, sectors));
    // Close the menu after navigating to a new page
    setDesktopMenuOpen(false);
  }, [activeHref, sectors]);

  const isMobilePinned = useScrollPinning({
    sentinelRef: mobilePinSentinelRef,
    enabled: showMobile,
    pinned,
    // 1px tolerance rather than 0: after the scroll-to-pin, the sentinel can
    // settle a sub-pixel below the top (e.g. 0.2px) due to fractional layout /
    // device-pixel rounding when embedded. A strict `<= 0` never activates in
    // that case, so the "expand once pinned" menu toggle would never fire. 1px
    // absorbs the rounding and is visually identical to 0.
    activateAt: 1,
    releaseAt: 12,
    schedule: 'animationFrame',
  });
  const isPinned = useScrollPinning({
    sentinelRef: desktopPinSentinelRef,
    releaseOffsetRef: desktopPinReleaseOffsetRef,
    transitionLockRef: desktopPinTransitionLockRef,
    enabled: showDesktop,
    pinned,
    activateAt: 0,
    releaseAt: 12,
  });

  const handleMenuToggle = () => {
    setDesktopMenuOpen((current) => !current);
    onMenuClick?.();
  };

  const desktopInteractionEnabled =
    mode === 'desktop' || (mode === 'responsive' && isDesktopViewport === true);
  const mobileInteractionEnabled =
    mode === 'mobile' || (mode === 'responsive' && isDesktopViewport === false);

  return {
    showMobile,
    showDesktop,
    forceDesktopLayout,
    mobileInteractionEnabled,
    desktopInteractionEnabled,
    mobilePinSentinelRef,
    desktopPinSentinelRef,
    desktopPinReleaseOffsetRef,
    desktopPinTransitionLockRef,
    isMobilePinned,
    isPinned,
    desktopBrowseOverlayOpen: desktopMenuOpen,
    activeDesktopTab,
    setActiveDesktopTab,
    handleMenuToggle,
    setDesktopBrowseOverlayOpen: setDesktopMenuOpen,
  };
}
